import { formatDistanceToNow, format } from 'date-fns'
import type { Timestamp } from 'firebase/firestore'

// Format price for display
export function formatPrice(price: number | null | undefined): string {
  if (price === null || price === undefined) {
    return 'Price on Application'
  }

  if (price === 0) {
    return '$0'
  }

  return new Intl.NumberFormat('en-NZ', {
    style: 'currency',
    currency: 'NZD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(price)
}

// Format price with cents
export function formatPriceWithCents(price: number): string {
  return new Intl.NumberFormat('en-NZ', {
    style: 'currency',
    currency: 'NZD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(price)
}

// Format relative time (e.g., "2 hours ago")
export function formatRelativeTime(date: Date | Timestamp | undefined): string {
  if (!date) return ''

  const jsDate = date instanceof Date ? date : date.toDate()
  return formatDistanceToNow(jsDate, { addSuffix: true })
}

// Format date (e.g., "15 Jan 2024")
export function formatDate(date: Date | Timestamp | undefined): string {
  if (!date) return ''

  const jsDate = date instanceof Date ? date : date.toDate()
  return format(jsDate, 'd MMM yyyy')
}

// Format time remaining for auctions
export function formatTimeRemaining(endDate: Date | Timestamp | undefined): string {
  if (!endDate) return ''

  const jsDate = endDate instanceof Date ? endDate : endDate.toDate()
  const now = new Date()
  const diff = jsDate.getTime() - now.getTime()

  if (diff <= 0) return 'Ended'

  const days = Math.floor(diff / (1000 * 60 * 60 * 24))
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))

  if (days > 0) {
    return `${days}d ${hours}h left`
  } else if (hours > 0) {
    return `${hours}h ${minutes}m left`
  } else {
    return `${minutes}m left`
  }
}

// Format number with commas
export function formatNumber(num: number): string {
  return new Intl.NumberFormat('en-NZ').format(num)
}

// Format odometer (e.g., "125,000 km")
export function formatOdometer(km: number | undefined): string {
  if (!km) return ''
  return `${formatNumber(km)} km`
}

// Format area (e.g., "150 m²")
export function formatArea(sqm: number | undefined): string {
  if (!sqm) return ''
  return `${formatNumber(sqm)} m²`
}

// Truncate text with ellipsis
export function truncateText(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text
  return text.slice(0, maxLength).trim() + '...'
}

// Format location for display
export function formatLocation(location: string, suburb?: string): string {
  if (suburb) {
    return `${suburb}, ${location}`
  }
  return location
}
